import React, { useEffect, useMemo } from 'react';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Loader2 } from 'lucide-react';

import { PackagingPOStatusCell } from 'components/packaging/components/packagingPOV2/PackagingPOStatusCell';
import { usePurchaseOrderItems } from 'components/procurement/components/purchaseOrdersV2/hooks/usePurchaseOrderItems';
import { usePurchaseOrderQuery } from 'components/procurement/components/purchaseOrdersV2/queries/PurchaseOrderQueries';
import {
    formatPurchaseOrderDate,
    formatPurchaseOrderDateTime,
} from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDateUtils';
import {
    FALLBACK_TEXT,
    formatNumber,
    normalizeOptionalText,
} from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDisplayUtils';
import { Badge } from 'shadcn/components/ui/Badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from 'shadcn/components/ui/Dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shadcn/components/ui/Table';

const EMPTY_ITEMS = [];

function NameCell({ getValue }) {
    const value = normalizeOptionalText(getValue());
    if (value === FALLBACK_TEXT) return <span className="text-left">{value}</span>;

    // Use native title — Radix Tooltip inside Dialog can leave body pointer-events:none
    // when @radix-ui/react-dismissable-layer versions disagree across the tree.
    return (
        <span className="block max-w-[300px] truncate text-left" title={value}>
            {value}
        </span>
    );
}

/**
 * Read-only modal showing partner purchase order line items.
 *
 * @param {Object} props
 * @param {Object|null} props.purchaseOrder - List-row purchase order (header fields + id for items fetch)
 * @param {boolean} props.open
 * @param {(open: boolean) => void} props.onOpenChange
 */
export function ViewPartnerPurchaseOrderLineItemsModal({ onOpenChange, open, purchaseOrder }) {
    const purchaseOrderId = purchaseOrder?.id;

    const {
        data: detail,
        error,
        isFetching,
        isLoading,
    } = usePurchaseOrderQuery({
        id: purchaseOrderId,
        enabled: open && Boolean(purchaseOrderId),
    });

    // Defensive cleanup: mismatched dismissable-layer versions can leave the page unclickable.
    // Run after Dialog's own cleanup so we overwrite a stale restore of pointer-events:none.
    useEffect(() => {
        if (open) {
            return undefined;
        }
        const timeoutId = window.setTimeout(() => {
            document.body.style.pointerEvents = '';
        }, 0);
        return () => window.clearTimeout(timeoutId);
    }, [open]);

    // Header always comes from the list row — detail responses often omit or reshape these fields.
    const headerSource = purchaseOrder ?? null;
    const rawItems = detail?.items ?? detail?.purchaseOrderItems ?? EMPTY_ITEMS;
    const items = usePurchaseOrderItems(rawItems);

    const headerFields = useMemo(() => {
        if (headerSource == null) return [];

        const fields = [
            { key: 'id', label: 'PO #', value: normalizeOptionalText(headerSource.id) },
            {
                key: 'status',
                label: null,
                value: headerSource.status != null ? normalizeOptionalText(headerSource.status) : FALLBACK_TEXT,
                render: (value) => <PackagingPOStatusCell status={value} fallbackText={FALLBACK_TEXT} />,
            },
            { key: 'facility', label: 'Facility', value: normalizeOptionalText(headerSource.facilityName) },
            { key: 'vendor', label: 'Vendor', value: normalizeOptionalText(headerSource.vendorName) },
            { key: 'pickup', label: 'Pick Up', value: formatPurchaseOrderDate(headerSource.pickupDate) },
            {
                key: 'delivery',
                label: 'Scheduled Delivery',
                value: formatPurchaseOrderDateTime(headerSource.scheduledDeliveryDate),
            },
        ];

        return fields.filter((field) => field.value !== FALLBACK_TEXT);
    }, [headerSource]);

    const columns = useMemo(
        () => [
            {
                id: 'externalSku',
                header: 'External SKU',
                accessorFn: (row) => row?.externalSku ?? null,
                cell: ({ getValue }) => normalizeOptionalText(getValue()),
            },
            {
                id: 'name',
                header: 'Name',
                accessorFn: (row) => row?.itemName ?? row?.name ?? null,
                cell: NameCell,
            },
            {
                id: 'caseQuantity',
                header: 'Case Quantity',
                accessorFn: (row) => row?.qty,
                cell: ({ getValue }) => formatNumber(getValue()),
            },
            {
                id: 'unitsPerCase',
                header: 'Units/Case',
                accessorFn: (row) => row?.baseUnitsPerCase,
                cell: ({ getValue }) => formatNumber(getValue()),
            },
            {
                id: 'totalUnits',
                header: 'Total Units',
                accessorFn: (row) => {
                    const qtyRaw = row?.qty;
                    const unitsPerCaseRaw = row?.baseUnitsPerCase;
                    if (qtyRaw == null || unitsPerCaseRaw == null) return null;
                    const qty = Number(qtyRaw);
                    const unitsPerCase = Number(unitsPerCaseRaw);
                    return Number.isFinite(qty) && Number.isFinite(unitsPerCase) ? qty * unitsPerCase : null;
                },
                cell: ({ getValue }) => formatNumber(getValue()),
            },
        ],
        [],
    );

    const table = useReactTable({
        data: items,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    const showItemsLoading = open && Boolean(purchaseOrderId) && (isLoading || (isFetching && !detail));

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="flex max-h-[85vh] flex-col overflow-hidden sm:max-w-3xl">
                <DialogHeader className="shrink-0">
                    <DialogTitle>Purchase Order Items</DialogTitle>
                    <DialogDescription className="sr-only">
                        Read-only line items for the selected purchase order.
                    </DialogDescription>
                    {headerFields.length > 0 && (
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pt-1">
                            {headerFields.map((field) =>
                                field.render ? (
                                    <React.Fragment key={field.key}>{field.render(field.value)}</React.Fragment>
                                ) : (
                                    <span key={field.key} className="text-sm text-muted-foreground">
                                        {field.label}:{' '}
                                        <span className="font-medium text-foreground">{field.value}</span>
                                    </span>
                                ),
                            )}
                        </div>
                    )}
                </DialogHeader>

                {error ? (
                    <div className="mt-4 shrink-0 rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
                        {error.message || 'Failed to load purchase order items.'}
                    </div>
                ) : null}

                {showItemsLoading ? (
                    <div className="mt-6 flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Loading line items…
                    </div>
                ) : (
                    <div data-testid="partner-po-line-items-scroll" className="mt-4 min-h-0 flex-1 overflow-y-auto">
                        <Table>
                            <TableHeader>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => (
                                            <TableHead key={header.id}>
                                                {header.isPlaceholder
                                                    ? null
                                                    : flexRender(header.column.columnDef.header, header.getContext())}
                                            </TableHead>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableHeader>
                            <TableBody>
                                {table.getRowModel().rows.length ? (
                                    table.getRowModel().rows.map((row) => (
                                        <TableRow key={row.id}>
                                            {row.getVisibleCells().map((cell) => (
                                                <TableCell key={cell.id} className="text-left">
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} className="h-24 text-center">
                                            No line items found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                        {items.length > 0 && (
                            <div className="mt-2">
                                <Badge variant="outline" className="text-xs">
                                    {items.length} {items.length === 1 ? 'item' : 'items'}
                                </Badge>
                            </div>
                        )}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
